import { Head, router, useForm } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

import CustomerForm from "./_form"

interface Option {
  id: number
  name: string
}

interface CustomerData {
  id: number
  company_name: string
  first_name: string
  last_name: string

  address: string
  address2: string
  city: string
  state: string
  zip: string
  office_phone: string
  cell_phone: string
  home_phone: string
  fax: string
  email: string
  website: string
  notes: string
  internal_notes: string
  client_type_id: number | null
  sales_rep_id: number | null
  referral_customer_id: number | null
  vendor_referral_id: number | null
  referral_label: string | null
}

interface Props {
  customer: CustomerData
  client_types: Option[]
  users: Option[]
  errors?: Record<string, string[]>
}

export default function Edit({ customer, client_types, users, errors }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: "Edit", href: `/customers/${customer.id}/edit` },
  ]

  const [referralString, setReferralString] = useState(() => {
    if (customer.referral_customer_id)
      return `C${customer.referral_customer_id}`
    if (customer.vendor_referral_id) return `V${customer.vendor_referral_id}`
    return ""
  })

  const { data, setData, processing } = useForm({
    company_name: customer.company_name || "",
    first_name: customer.first_name || "",
    last_name: customer.last_name || "",
    address: customer.address || "",
    address2: customer.address2 || "",
    city: customer.city || "",
    state: customer.state || "",
    zip: customer.zip || "",
    office_phone: customer.office_phone || "",
    cell_phone: customer.cell_phone || "",
    home_phone: customer.home_phone || "",
    fax: customer.fax || "",
    email: customer.email || "",
    website: customer.website || "",
    notes: customer.notes || "",
    internal_notes: customer.internal_notes || "",
    client_type_id: customer.client_type_id,
    sales_rep_id: customer.sales_rep_id,
  })

  const handleReferralChange = (refString: string) => {
    setReferralString(refString)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const params: Record<string, unknown> = {}
    if (referralString) {
      params.referral_string = referralString
    }
    router.patch(`/customers/${customer.id}`, { customer: data, ...params })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Edit Customer" />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">Edit Customer</h1>

        <CustomerForm
          data={data}
          setData={setData}
          errors={errors}
          processing={processing}
          onSubmit={handleSubmit}
          submitLabel="Update Customer"
          client_types={client_types}
          users={users}
          referralString={referralString}
          onReferralChange={handleReferralChange}
          initialReferralLabel={customer.referral_label}
        />
      </div>
    </AppLayout>
  )
}
